import { nums } from './nums';
import * as attributeTypes from '~/config/settings/attribute_types.settings';
import * as reporting from '~/config/settings/reporting.settings';

/**
 * A dictionary of replacement labels. Key-value pairs where the key is the original label
 * and the value is the replacement label.
 */
interface ReplacementLabels {
  [key: string]: string;
}

interface SelectableOptions {
  value?: string;
  label?: string;
  replacementLabels?: ReplacementLabels;
}

interface User {
  id: number;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Converts an input array to an array of `{ value, label }` pairs based on specified options.
 *
 * @param list The input array to convert.
 * @param options The options for the conversion.
 * @returns The converted array of `{ value, label }` pairs.
 *
 * @example
 * // input array:
 * const myList = [
 *   {
 *     id: 123,
 *     name: 'My Thing',
 *     extra: 'other stuff'
 *   }
 * ];
 *
 * // output array:
 * const selectableList = convert.toSelectable(myList);
 * // [
 * //   {
 * //     value: 123,
 * //     label: 'My Thing'
 * //   }
 * // ]
 *
 * @example
 * // With custom options and replacement labels:
 * const selectableList = convert.toSelectable(myList, {
 *   value: 'customId',
 *   label: 'customName',
 *   replacementLabels: {
 *     'Manager Employee Id': 'People Leader'
 *   }
 * });
 * This will use the label "People Leader" for any attribute that is labelled
 * "Manager Employee Id" by default.
 */

const toSelectable = <T extends Record<string, any>>(list: T[], options: SelectableOptions = {}) => {
  const { value = 'id', label = 'name', replacementLabels = {} } = options;

  if (!list || !Array.isArray(list)) {
    return [];
  }
  const filteredList = list.filter((el) => el !== null && typeof el === 'object' && !nums.isNumberNaN(el.id));
  const convertedList = filteredList.map((el) => {
    let updatedLabel = (el && el[label]) || '';

    if (replacementLabels && updatedLabel in replacementLabels) {
      updatedLabel = replacementLabels[updatedLabel];
    }

    return {
      value: el[value],
      label: updatedLabel,
    };
  });

  const sortedList = convertedList.sort((a, b) => a.label.localeCompare(b.label));

  return sortedList;
};

/**
 * Converts an input array, mapping attributes to new labels based on the specified options.
 *
 * @param list The input array to convert.
 * @param options The options for the conversion.
 * @returns The converted array with updated labels.
 */
const toAcceptableLabels = <T extends { id: number; name: string }>(
  list: T[],
  lang: string,
  options: SelectableOptions = {},
): (T & { translations: Record<string, { name: string }> })[] => {
  const { label = 'name', replacementLabels = {} } = options;

  if (!list || !Array.isArray(list)) {
    return [];
  }

  const filteredList = list.filter((el) => el !== null && typeof el === 'object' && !nums.isNumberNaN(el.id));

  const convertedList = filteredList.map((el) => {
    let updatedLabel = (el && el[label]) || '';

    let translations = {};
    // @ts-ignore @TODO Epic WT-21600
    if (el.translations && typeof el.translations === 'object') {
      // @ts-ignore @TODO Epic WT-21600
      translations = { ...el.translations };
    }
    if (replacementLabels && updatedLabel in replacementLabels) {
      updatedLabel = replacementLabels[updatedLabel];
    }
    return {
      ...el,
      name: updatedLabel,
      translations: {
        ...translations,
        [lang]: {
          name: updatedLabel,
        },
      },
    };
  });
  return convertedList;
};
/**
 * Converts an object into a new object that resembles a user object.
 * @param obj The object to convert.
 * @returns The converted user object.
 */
const toUser = (obj: Record<string, any>): User => ({
  id: obj.userId,
  name: obj.name,
  firstName: obj.firstName,
  lastName: obj.lastName,
  email: obj.email,
});

/**
 * Converts an object into a new object with all keys transformed to lower case.
 * @param obj The object to convert.
 * @returns The converted object with lower case keys.
 */
const toLowerKeys = (obj: Record<string, any>): Record<string, any> => {
  return Object.keys(obj).reduce(
    (acc, key) => ({
      ...acc,
      [key.toLowerCase()]: obj[key],
    }),
    {},
  );
};

/**
 * Converts an array of attributes into an attribute entity dictionary.
 *
 * @param attributes The array of attributes to convert.
 * @returns The attribute entity dictionary.
 */
const attributesToEntities = (
  attributes: { id: string; values: string[] }[],
): Record<number, { attributeId: number; values: string[] }> => {
  return attributes.reduce((accumulator, attribute) => {
    const attributeId = Number(attribute.id);

    return {
      ...accumulator,
      [attributeId]: {
        attributeId,
        values: attribute.values,
      },
    };
  }, {});
};

/**
 * Converts an attribute type string to an HTML <input> type string.
 *
 * @param attributeType The attribute type string.
 * @returns The corresponding HTML <input> type string.
 */
const attributeTypeToInputType = (attributeType: string): string => {
  switch (attributeType) {
    case attributeTypes.keys.DATE:
      return 'date';
    case attributeTypes.keys.EMAIL:
      return 'email';
    case attributeTypes.keys.BOOLEAN:
      return 'boolean';
    case attributeTypes.keys.STRING:
    case attributeTypes.keys.NUMBER:
    default:
      return 'text';
  }
};

/**
 * Remove all "empty" attributes from an object.
 *
 * Checks if the attribute is falsy or an object with no keys.
 *
 * @param obj The original object from which to remove empty attributes.
 * @returns A new object that contains everything from `obj` but with empty attributes removed.
 */
const removeEmpty = (obj: Record<string, any>): Record<string, any> => {
  const keys = Object.keys(obj);

  return keys.reduce((nonEmptyObj, key) => {
    if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
      return nonEmptyObj;
    }

    if (typeof obj[key] === 'object' && Object.keys(obj[key]).length === 0) {
      return nonEmptyObj;
    }

    return {
      ...nonEmptyObj,
      [key]: obj[key],
    };
  }, {});
};

/**
 * Change a single translated value within a translations object and return
 * a new translations object containing the change.
 *
 * @param lang The language code of the translation to change.
 * @param name The name of the translated value to change.
 * @param value The new value to assign to the translated value.
 * @param translations The original translations object.
 * @returns A new translations object with the updated translated value.
 *
 * @example translations object structure
 * translations: {
 *   'en-ca': {
 *     title: 'English Title',
 *     description: 'English description goes here'
 *   },
 *   'fr-ca': {
 *     title: 'French Title',
 *     description: 'French description goes here'
 *   }
 * }
 *
 * @example usage
 * const newTranslations = changeTranslationValue('en-ca', 'title', 'New Title', translations);
 * newTranslations['en-ca'].title === 'New Title'; // true
 */
// TODO: Maybe this should belong in a dedicated module for manipulating "translations"
const changeTranslationValue = (
  lang: string,
  name: string,
  value: string,
  translations: Record<string, Record<string, string>>,
): Record<string, Record<string, string>> => {
  return {
    ...translations,
    [lang]: {
      ...translations[lang],
      [name]: value,
    },
  };
};

/**
 * Take a translations object and return a new translations object with all its
 * values `.trim()`ed.
 *
 * @param translations The translations object to trim.
 * @returns A new translations object with trimmed values.
 *
 * @example translations object structure
 * translations: {
 *   'en-ca': {
 *     title: '  English Title  ',
 *     description: '  English description goes here  '
 *   },
 *   'fr-ca': {
 *     title: '  French Title  ',
 *     description: '  French description goes here  '
 *   }
 * }
 *
 * @example usage
 * const trimmedTranslations = trimTranslations(translations);
 * console.log(trimmedTranslations);
 * // {
 * //   'en-ca': {
 * //     title: 'English Title',
 * //     description: 'English description goes here'
 * //   },
 * //   'fr-ca': {
 * //     title: 'French Title',
 * //     description: 'French description goes here'
 * //   }
 * // }
 *
 */
const trimTranslations = (
  translations: Record<string, Record<string, string>>,
): Record<string, Record<string, string>> => {
  const translatedLangs = Object.keys(translations);

  return translatedLangs.reduce((updatedTranslations, lang) => {
    const translatedProperties = Object.keys(translations[lang]);

    return {
      ...updatedTranslations,
      [lang]: translatedProperties.reduce((updatedValues, key) => {
        const value = translations[lang][key];
        const trimmedValue = typeof value === 'string' ? value.trim() : value; // Don't trim non-string values

        return {
          ...updatedValues,
          [key]: trimmedValue,
        };
      }, {}),
    };
  }, {});
};

/**
 * Pick specified keys from an object and return a new object with only the picked keys.
 *
 * @param obj The object from which to pick keys.
 * @param pickedKeys An array of keys to pick from the object.
 * @returns A new object with only the picked keys.
 *
 * @example
 * const obj = {
 *   name: 'John',
 *   age: 30,
 *   email: 'john@example.com'
 * };
 *
 * const pickedObj = pick(obj, ['name', 'email']);
 * console.log(pickedObj);
 * // {
 * //   name: 'John',
 * //   email: 'john@example.com'
 * // }
 */
const pick = <T extends Record<string, unknown>>(obj: T | undefined | null, pickedKeys: string[]): Partial<T> => {
  if (!obj) return {};
  return Object.keys(obj).reduce((pickedObj, key) => {
    if (!pickedKeys.includes(key)) return pickedObj;

    return {
      ...pickedObj,
      [key]: obj[key as keyof T],
    };
  }, {});
};

/**
 * Merge an array of objects with a dictionary object based on a specified field.
 *
 * @param list The array of objects to merge.
 * @param dict The dictionary object to merge with.
 * @param field The field used to match objects from the array with the dictionary (default: 'id').
 * @returns An array of merged objects.
 *
 * @example
 * const list = [
 *   { id: 1, name: 'John' },
 *   { id: 2, name: 'Jane' }
 * ];
 *
 * const dict = {
 *   1: { role: 'Manager' },
 *   2: { role: 'Employee' }
 * };
 *
 * const mergedList = toMergedList(list, dict, 'id');
 * console.log(mergedList);
 * // [
 * //   { id: 1, name: 'John', role: 'Manager' },
 * //   { id: 2, name: 'Jane', role: 'Employee' }
 * // ]
 */
const toMergedList = <T extends Record<string, unknown>>(
  list: T[],
  dict: Record<string, Partial<T>>,
  field = 'id',
): (T & Partial<T>)[] => {
  const mergedList = list.map((entry) => {
    return {
      // @ts-ignore @TODO Epic WT-21600
      ...dict[entry[field]],
      ...entry,
    };
  });

  return mergedList;
};

/**
 * Convert an attribute type string to a sample value based on the type.
 *
 * @param attributeType The attribute type string.
 * @returns A sample value corresponding to the attribute type.
 *
 * @example
 * const sampleValue = attributeTypeToSampleValue('STRING');
 * console.log(sampleValue); // 'Example String'
 */
const attributeTypeToSampleValue = (attributeType: string): boolean | number | string => {
  switch (attributeType) {
    case 'BOOLEAN':
      return true;
    case 'NUMBER':
      return 123;
    case 'EMAIL':
      return 'test@worktango.io';
    case 'DATE':
      return 'YYYY-MM-DD';
    case 'STRING':
    default:
      return 'Example String';
  }
};

/**
 * Generate a unique key for compare result based on the provided options.
 *
 * @param options The options object containing categoryId, surveyElementId, attributeId, isHierarchyAttribute, and attributeValueId.
 * @returns A unique key for compare result.
 *
 * @example
 * const options = {
 *   categoryId: 1,
 *   surveyElementId: 2,
 *   attributeId: 3,
 *   isHierarchyAttribute: true,
 *   attributeValueId: 4
 * };
 *
 * const compareResultKey = toCompareResultKey(options);
 * console.log(compareResultKey); // '1-2-3-true-4'
 */
const toCompareResultKey = (options?: {
  categoryId?: number;
  surveyElementId?: number;
  attributeId?: number;
  isHierarchyAttribute?: boolean;
  attributeValueId?: number;
}): string => {
  const categoryId = options?.categoryId ?? undefined;
  const surveyElementId = options?.surveyElementId ?? undefined;
  const attributeId = options?.attributeId ?? undefined;
  const isHierarchyAttribute = options?.isHierarchyAttribute ?? false;
  const attributeValueId = options?.attributeValueId ?? undefined;
  return `${categoryId}-${surveyElementId}-${attributeId}-${isHierarchyAttribute}-${attributeValueId}`;
};

/**
 * Convert a reporting analyze specification and type to a key for analyze compare differ.
 *
 * @param spec The reporting analyze specification.
 * @param type The reporting analyze type.
 * @returns A key for analyze compare differ.
 *
 * @example
 * const analyzeSpec = reporting.analyzeSpecs.ENPS;
 * const analyzeType = reporting.analyzeTypes.DISTRIBUTION;
 *
 * const compareDifferKey = toAnalyzeCompareDifferKey(analyzeSpec, analyzeType);
 * console.log(compareDifferKey); // 'enpsFavorableDiffer'
 */
const toAnalyzeCompareDifferKey = (spec: string | undefined, type: string | undefined): string => {
  switch (spec) {
    case reporting.analyzeSpecs.ENPS:
      // for eNPS compare, always show and export eNPS score different, as promoter difference not meaningful
      return 'enpsScoreDiffer';
    case reporting.analyzeSpecs.AVG:
      return 'scoreAverageDiffer';
    case reporting.analyzeSpecs.PERCENT:
    default:
      return 'scaleFavorableDiffer';
  }
};

/**
 * Normalize a list of strings and make the result hashable by sorting and joining the strings.
 *
 * @param stringArray The array of strings to be normalized.
 * @returns The normalized and hashable string representation of the input array.
 *
 * @example
 * const stringArray = ['banana', 'apple', 'orange'];
 *
 * const normalizedString = normalizeStrings(stringArray);
 * console.log(normalizedString); // 'apple,banana,orange'
 */
const normalizeStrings = (stringArray: string[]): string => {
  const sortedArray = [...stringArray].sort();
  return sortedArray.join(',');
};

/**
 * Convert an array of objects into a dictionary based on a specified key field.
 *
 * @param objectList The array of objects to be converted into a dictionary.
 * @param keyField The field name to be used as the key for the dictionary.
 * @returns The resulting dictionary where each key corresponds to an object from the input array.
 *
 * @example
 * const objectList = [
 *   { id: 1, name: 'John' },
 *   { id: 2, name: 'Jane' },
 *   { id: 3, name: 'Bob' }
 * ];
 *
 * const dictionary = toDict(objectList, 'id');
 * console.log(dictionary);
 * // {
 * //   1: { id: 1, name: 'John' },
 * //   2: { id: 2, name: 'Jane' },
 * //   3: { id: 3, name: 'Bob' }
 * // }
 */
const toDict = <T extends Record<string, any>>(objectList: T[], keyField: keyof T): Record<string, T> => {
  return objectList.length && objectList[0][keyField]
    ? objectList.reduce((acc: Record<string, T>, object: T) => {
        acc[String(object[keyField])] = object;
        return acc;
      }, {})
    : {};
};

/**
 * Convert an array of objects into a dictionary based on a specified key
 * field to a single value.
 *
 * @param objectList The array of objects to be converted into a dictionary.
 * @param keyField The field name to be used as the key for the dictionary.
 * @param valueField The field name to be used as the value for the dictionary.
 * @returns The resulting dictionary where each key corresponds to a value from
 * an object in the input array.
 *
 * @example
 * const objectList = [
 *   { id: 1, name: 'John' },
 *   { id: 2, name: 'Jane' },
 *   { id: 3, name: 'Bob' }
 * ];
 *
 * const dictionary = toDictValue(objectList, 'id', 'name');
 * console.log(dictionary);
 * // {
 * //   1: 'John',
 * //   2: 'Jane',
 * //   3: 'Bob'
 * // }
 */
const toDictValue = <T extends Record<string, any>>(
  objectList: T[],
  keyField: keyof T,
  valueField: keyof T,
): Record<string, T> => {
  return objectList.length && objectList[0][keyField]
    ? objectList.reduce((acc: Record<string, T>, object: T) => {
        acc[String(object[keyField])] = object[valueField];
        return acc;
      }, {})
    : {};
};

export const convert = {
  toSelectable,
  toAcceptableLabels,
  toUser,
  toLowerKeys,
  attributesToEntities,
  attributeTypeToInputType,
  removeEmpty,
  changeTranslationValue,
  trimTranslations,
  pick,
  toMergedList,
  attributeTypeToSampleValue,
  toCompareResultKey,
  toAnalyzeCompareDifferKey,
  normalizeStrings,
  toDict,
  toDictValue,
};
